import NumberHelper from "@utils/Helpers/NumberHelper.js";

/**
 *
 */
export default class Gfr {
  constructor() {}

  /**
   * Sets the patient gender
   *
   * @param gender
   * @return {Gfr}
   */
  setGender(gender) {
    this.gender = gender;

    return this;
  }

  /**
   * Sets the age
   *
   * @param age
   * @return {Gfr}
   */
  setAge(age) {
    this.age = age;

    return this;
  }

  /**
   * Sets the weight
   *
   * @param weight
   */
  setWeight(weight) {
    this.weight = weight;

    return this;
  }

  /**
   * Sets the creatinie
   *
   * @param creatinine
   * @return {Gfr}
   */
  setCreatinine(creatinine) {
    this.creatinine = creatinine;

    return this;
  }

  /**
   * Calculates the gfr
   *
   * @return {*}
   */
  getGfr(formal) {
    if (this.gender === "" || this.age === "") {
      this.missingAttributes = true;
      return null;
    }

    switch (formal) {
      case "useCkdEpi": {
        return this._getGfrByCkdEpi();
      }
      case "useMayoClinic": {
        return this._getGfrByMayoClinic();
      }
      case "useMdrd": {
        return this._getGfrByMdrd();
      }
      case "useMdrdBlackEthnic": {
        return this._getGfrByMdrdBlackEthnic();
      }
      case "useCockcroftGault": {
        return this._getGfrByCockroftGault();
      }
      // No default
    }

    return null;
  }

  /**
   * CKD-EPI
   * https://flexikon.doccheck.com/de/CKD-EPI-Formel
   *
   * @return {*}
   * @private
   */
  _getGfrByCkdEpi(round = true) {
    // 142 * (creatinine / A)^B x 0.9938^age    * 1.012 if female

    const creatinineFactor = NumberHelper.formatStringAsFloat(this.creatinine);
    let factorA, factorB;
    if (this.gender === "female") {
      factorA = 0.7;
      factorB = creatinineFactor <= 0.7 ? -0.241 : -1.2;
    } else {
      factorA = 0.9;
      factorB = creatinineFactor <= 0.9 ? -0.302 : -1.2;
    }

    let gfr = 142 * Math.pow(creatinineFactor / factorA, factorB) * Math.pow(0.9938, Number.parseInt(this.age));

    if (this.gender === "female") {
      gfr *= 1.012;
    }

    return round ? NumberHelper.round(gfr) : gfr;
  }

  /**
   * Mayo-Klinik-Formel
   * https://flexikon.doccheck.com/de/Mayo-Klinik-Formel
   *
   * @return {*}
   * @private
   */
  _getGfrByMayoClinic() {
    let creatinineFactor = NumberHelper.formatStringAsFloat(this.creatinine);

    if (creatinineFactor <= 0) {
      return null;
    }

    const genderFactor = this.gender === "female" ? 0.205 : 0;
    const ageFactor = Number.parseInt(this.age);

    if (creatinineFactor < 0.8) {
      creatinineFactor = 0.8;
    }

    const gfr = Math.exp(
      1.911 + 5.249 / creatinineFactor - 2.114 / Math.pow(creatinineFactor, 2) - 0.006_86 * ageFactor - genderFactor,
    );

    return NumberHelper.round(gfr);
  }

  /**
   * MDRD calculation
   * https://flexikon.doccheck.com/de/MDRD-Formel
   *
   * @return {*}
   * @private
   */
  _getGfrByMdrd(round = true) {
    const genderFactor = this.gender === "female" ? 0.742 : 1;
    const ageFactor = Math.pow(Number.parseInt(this.age), -0.203);
    const creatinineFactor = Math.pow(NumberHelper.formatStringAsFloat(this.creatinine), -1.154);

    const gfr = 186 * creatinineFactor * ageFactor * genderFactor;

    return round ? NumberHelper.round(gfr) : gfr;
  }

  /**
   * MDRD black etnic calculation
   * https://flexikon.doccheck.com/de/MDRD-Formel
   *
   * @return {*}
   * @private
   */
  _getGfrByMdrdBlackEthnic() {
    const gfrWithoutBlackEthnic = this._getGfrByMdrd(false);
    return NumberHelper.round(gfrWithoutBlackEthnic * 1.212);
  }

  /**
   * Cockroft Gault
   * https://flexikon.doccheck.com/de/Cockcroft-Gault-Formel
   *
   * @return {*}
   * @private
   */
  _getGfrByCockroftGault() {
    if (this.weight === "" || this.weight === 0) {
      this.missingAttributes = true;
      return null;
    } else {
      this.missingAttributes = false;

      const genderFactor = this.gender === "female" ? 0.85 : 1;
      const ageFactor = 140 - Number.parseInt(this.age);
      const weightFactor = NumberHelper.formatStringAsFloat(this.weight) / 72;
      const creatinineFactor = NumberHelper.formatStringAsFloat(this.creatinine);

      return NumberHelper.round((ageFactor / creatinineFactor) * weightFactor * genderFactor);
    }
  }
  missingAttributes = false;
  gender = "male";
  age = 0;
  weight = 0;
  creatinine = 0;
}

// Initialize the gfr instance
const gfrCalculator = new Gfr();

export {gfrCalculator};
